import React, { useEffect } from 'react'
import styled from 'styled-components'

import 'src/styles/lp-main.scss'
import { navigate } from 'gatsby'
import OfferBanner from 'src/components/common/OfferBanner'
import { useDispatch, useSelector } from 'react-redux'
import { deleteParam, selectParams, updateParams } from 'src/modules/reducers/params'

import { selectCountryCode } from 'src/modules/reducers/market'
import { Helmet } from 'react-helmet'
import { useLocation } from '@reach/router'
import useTrackScroll from 'src/modules/hooks/useTrackScroll'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import { initTranscend } from 'src/modules/reducers/transcend/actions'
import { ratePlanCodes } from 'src/constants'
import { useAnalyticsInit } from 'src/modules/hooks/useAnalyticsInit'
import FooterSeo from '../Footer/FooterSeo'

const Layout = ({
  lang,
  children,
  market,
  offerCode,
  offerBanner,
  addonCode,
  ctaHrefOverride,
  // Everything below should eventually be replaced
  miceText,
  noFooter,
  // Charter Locale Switcher
  noteText,
  // Disabled Geo redirect
  noRedirect,
  // Add lang param
  forceLang,
  // CA Redirect URL
  caRedirectUrl,
  // US Redirect URL
  usRedirectUrl,
  // Personalization Params
  sportId,
  leagueId,
  teamId,
  noIndex,
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(initTranscend())
  }, [])

  const location = useLocation() || (typeof window !== 'undefined' && window.location)
  useAnalyticsInit({ lang, market, sportId, teamId, leagueId })

  // Add preselect param for overriding offer code preselect
  const params = useSelector(selectParams)
  const preselect = params?.preselect

  // Try to avoid race conditions here
  // I will want to seperate this offer param to a separate object or store
  React.useEffect(() => {
    if (preselect && ratePlanCodes[preselect]) {
      const offerParam = {
        offer: ratePlanCodes[preselect],
      }
      dispatch(updateParams(offerParam))
    } else if (offerCode) {
      const offerParam = {
        offer: offerCode,
      }
      dispatch(updateParams(offerParam))
    }
  }, [offerCode, preselect])

  React.useEffect(() => {
    if (addonCode) {
      const addonParam = {
        'addons[]': addonCode,
      }
      dispatch(updateParams(addonParam))
    }
  }, [addonCode])

  React.useEffect(() => {
    if (forceLang) {
      const addonParam = {
        lang: forceLang,
      }
      dispatch(updateParams(addonParam))
    } else {
      dispatch(deleteParam('lang'))
    }
  }, [forceLang])

  // Track scroll event
  useTrackScroll()

  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const countryCode = useSelector(selectCountryCode)

  // BE REALLY CAREFUL WITH THIS REDIRECT, OR ELSE CERTAIN LPs CAN RECEIVE THE WRONG MARKET
  React.useEffect(() => {
    // check market and country code and redirect if needed
    if (
      !noRedirect &&
      countryCode &&
      market?.toLowerCase() !== 'ca' &&
      market?.toLowerCase() !== 'canada' &&
      market?.toLowerCase() !== 'all' &&
      countryCode.toLowerCase() === 'ca'
    ) {
      const redirectDestination = caRedirectUrl || '/ca/redirect/'

      fireUIInteractionEvent(
        {
          component: 'lp_redirect_us_to_ca',
        },
        stateForProtobuf
      )
      navigate(redirectDestination + window.location.search, { replace: true })
    }
    if (
      !noRedirect &&
      countryCode &&
      market?.toLowerCase() !== 'us' &&
      market?.toLowerCase() !== 'all' &&
      countryCode.toLowerCase() === 'us' &&
      usRedirectUrl
    ) {
      const redirectDestination = usRedirectUrl || '/tv/'

      fireUIInteractionEvent(
        {
          component: 'lp_redirect_ca_to_us',
        },
        stateForProtobuf
      )
      navigate(redirectDestination + window.location.search, { replace: true })
    }
    if (
      !noRedirect &&
      countryCode &&
      market?.toLowerCase() !== 'es' &&
      market?.toLowerCase() !== 'spain' &&
      market?.toLowerCase() !== 'all' &&
      countryCode.toLowerCase() === 'es'
    ) {
      const redirectDestination = '/es/error/'

      fireUIInteractionEvent(
        {
          component: 'lp_redirect_es',
        },
        stateForProtobuf
      )
      navigate(redirectDestination + window.location.search, { replace: true })
    }
  }, [countryCode])

  // Update personalization params
  useEffect(() => {
    if (sportId) {
      dispatch(updateParams({ sportId: sportId }))
    }
    if (leagueId) {
      dispatch(updateParams({ leagueId: leagueId }))
    }
    if (teamId) {
      dispatch(updateParams({ teamId: teamId }))
    }
  }, [sportId, leagueId, teamId])

  // get base url of domain and page slug
  const baseUrl = 'https://www.fubo.tv'
  const pageSlug = location.pathname
  const pageUrl = baseUrl + pageSlug
  return (
    <LayoutWrapper>
      <Helmet>
        <link rel="canonical" href={pageUrl} />
        <meta property="og:url" content={pageUrl} />

        {noIndex && <meta name="robots" content="noindex" />}
      </Helmet>

      {offerBanner && (
        <OfferBanner lang={lang} details={offerBanner} ctaHrefOverride={ctaHrefOverride} />
      )}
      <main>{children}</main>

      {!noFooter && <FooterSeo lang={lang} market={market} miceText={miceText} />}
    </LayoutWrapper>
  )
}

export default Layout

const LayoutWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  min-height: 100vh;
  > main {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
`
